/**
 * Server-confirmed registration details shown on /masterclass/thank-you.
 *
 * Hard rules (asserted by tests/e2e/masterclass-thank-you-details.spec.ts):
 * - Only values the server returned are displayed — never what the visitor
 *   typed, and never a cached copy of a previous registration.
 * - Confirmations older than CONFIRMATION_MAX_AGE_MS are treated as stale and
 *   nothing is rendered (covers back-navigation and long-lived history state).
 * - No confirmation state at all (direct visit, email link) renders nothing.
 */
import { useLocation } from "react-router-dom";
import { BadgeCheck } from "lucide-react";
import {
  CONFIRMATION_MAX_AGE_MS,
  type MasterclassConfirmation,
  type MasterclassConfirmationState,
} from "@/lib/masterclass-registration";

function readState(state: unknown): MasterclassConfirmationState | null {
  if (!state || typeof state !== "object") return null;
  const s = state as Partial<MasterclassConfirmationState>;
  if (!s.registration || typeof s.registration !== "object") return null;
  if (typeof s.issuedAt !== "number" || !Number.isFinite(s.issuedAt)) return null;
  const age = Date.now() - s.issuedAt;
  if (age < 0 || age > CONFIRMATION_MAX_AGE_MS) return null;
  return s as MasterclassConfirmationState;
}

const ROWS: Array<{ key: keyof MasterclassConfirmation; label: string }> = [
  { key: "name", label: "Registered name" },
  { key: "email", label: "Confirmation sent to" },
  { key: "sessionDate", label: "Session date" },
  { key: "sessionTime", label: "Session time" },
  { key: "confirmationId", label: "Confirmation ID" },
];

export function MasterclassRegistrationDetails() {
  const { state } = useLocation();
  const confirmed = readState(state);
  if (!confirmed) return null;

  const rows = ROWS.filter(({ key }) => Boolean(confirmed.registration[key]));
  if (rows.length === 0) return null;

  return (
    <section
      aria-labelledby="registration-details-title"
      data-testid="registration-details"
      className="mb-10 border border-gold/25 bg-black/30 p-6 md:p-7"
    >
      <div className="flex items-center gap-2">
        <BadgeCheck className="h-4 w-4 text-gold" aria-hidden />
        <h2
          id="registration-details-title"
          className="font-display uppercase text-white text-sm tracking-[0.18em]"
        >
          Your registration
        </h2>
      </div>

      <dl className="mt-5 grid gap-4 sm:grid-cols-2">
        {rows.map(({ key, label }) => (
          <div key={key}>
            <dt className="text-[10px] uppercase tracking-[0.2em] text-gold">{label}</dt>
            <dd
              data-testid={`registration-${key}`}
              className="text-white text-sm mt-1 break-words"
            >
              {confirmed.registration[key]}
            </dd>
          </div>
        ))}
      </dl>

      {confirmed.registration.joinUrl && (
        <a
          href={confirmed.registration.joinUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="registration-joinUrl"
          className="mt-6 inline-flex items-center gap-2 border border-gold/40 px-4 py-2 text-xs uppercase tracking-[0.2em] text-gold hover:bg-gold/10 transition-colors"
        >
          Open your join link
        </a>
      )}
    </section>
  );
}
